import type { ReactElement } from "react";
import { Hinweis } from "@kundeninfo/ui/me-design";
import {
  EinheitFeld, LayEingabe, LayKnopf, TextFeld, WahlFeld, ZahlFeld, feldId,
} from "./felder.js";
import { OHNE_FAEHIGKEIT } from "./Marke.js";
import {
  fussAnzeige, fussSumme, fussSpaltenMitAnzahl, mitFussBreite, mitFussSeitentyp, mitWert, wertAn, zahlAnzeige,
  zahlAus,
} from "../layoutEntwurf.js";
import type { Entwurf, FussSeitentyp, FussSpalte } from "../layoutEntwurf.js";
import type { PanelProps } from "../LayoutRegister.js";

/**
 * Register „Seite": die vier Ränder in mm (5..40), Systemwert als Platzhalter — und, wenn die
 * Engine Belegdesign Etappe 2 kennt (`faehig`), Kopf- und Fußbereich je Seitentyp und der Beginn
 * der Positionstabelle; kennt sie auch den Fuß je Seitentyp (`fussFaehig`, Belegdesign Fußbereich),
 * die Gruppe „Fußzeile" mit Seite 1 und Folgeseiten (Entscheide 75, 240, 243).
 */
const RAENDER = [
  { id: "oben", label: "Oberer Rand" }, { id: "rechts", label: "Rechter Rand" },
  { id: "unten", label: "Unterer Rand" }, { id: "links", label: "Linker Rand" },
] as const;

const SEITEN = [
  { id: "ersteSeiteMm", text: "Seite 1" }, { id: "folgeseitenMm", text: "Folgeseiten" },
] as const;

/** Die Platzhalter der Fußspalten — beide Seitentypen (rechnungslayout ≥ ecb1a82, Vertragszusatz 20.10.2026). */
const PLATZHALTER = [
  { name: "{fusslinks}", text: "Fußblock links aus Verwaltung › Unternehmen (Zeilen untereinander; leer = nichts)" },
  { name: "{fussmitte}", text: "Fußblock Mitte" },
  { name: "{fussrechts}", text: "Fußblock rechts" },
  { name: "{firmenname}", text: "Name des Unternehmens, fett in der Akzentfarbe" },
  { name: "{belegart}", text: "Rechnung, Korrekturrechnung, Angebot oder Auftragsbestätigung" },
  { name: "{belegnummer}", text: "Belegnummer (bei Entwürfen „Entwurf“)" },
  { name: "{belegdatum}", text: "Belegdatum (TT.MM.JJJJ, bei Entwürfen leer)" },
  { name: "{seite}", text: "aktuelle Seitenzahl" },
  { name: "{seiten}", text: "Gesamtzahl der Seiten" },
  { name: "{firmenfuss}", text: "Firmenfuß: auf Seite 1 Name · Anschrift, USt-IdNr., IBAN · BIC und die Fußzeile, auf Folgeseiten die Fußzeile" },
  { name: "{fusszeile}", text: "die bisherige Fußzeile (Register Texte)" },
] as const;

/** Ein mm-Wert aus dem Entwurf („18.5mm“), sonst der Systemwert; Zahl in mm. */
function mmWert(entwurf: Entwurf, pfad: readonly string[], system: string): number {
  const n = Number(zahlAus(wertAn(entwurf, pfad), "mm"));
  return zahlAus(wertAn(entwurf, pfad), "mm") !== "" && Number.isFinite(n)
    ? n : Number(zahlAus(system, "mm"));
}

function mmText(mm: number): string {
  return String(Math.round(mm * 10) / 10).replace(".", ",");
}

/** Befunde mit diesem Pfadanfang, die kein Feld der Gruppe trägt — sie sollen nicht
 *  verschwinden, obwohl der Reiter sie zählt. */
export function restBefunde(fehler: Record<string, string>, anfang: string, behandelt: (pfad: string) => boolean):
    [string, string][] {
  return Object.entries(fehler).filter(([pfad]) =>
    (pfad === anfang || pfad.startsWith(`${anfang}.`) || pfad.startsWith(`${anfang}[`))
    && !behandelt(pfad));
}

export function Befunde(p: { liste: [string, string][] }): ReactElement | null {
  return p.liste.length === 0 ? null : (
    <>
      {p.liste.map(([pfad, text]) => (
        <span key={pfad} className="lay__feldfehler" role="alert">{text}</span>
      ))}
    </>
  );
}

export function SeitePanel(
  p: PanelProps & {
    faehig?: boolean | undefined; fussFaehig?: boolean | undefined;
    /** Öffnet das Unternehmen des Layouts (Verwaltung › Unternehmen) — Verweis der Warnung „Fußblöcke leer". */
    onUnternehmen?: (() => void) | undefined;
  },
): ReactElement {
  return (
    <div className="lay__panel">
      <p className="lay__panelhinweis">Ränder in Millimetern ab Blattkante (5 bis 40 mm).</p>
      {RAENDER.map((r) => {
        const pfad = `seitenraender.${r.id}`;
        return (
          <EinheitFeld key={r.id} id={feldId(pfad)} label={r.label} einheit="mm"
            wert={wertAn(p.entwurf, ["seitenraender", r.id])}
            systemwert={p.stand.katalog.seitenraender[r.id]}
            fehler={p.feldFehler[pfad]}
            onWert={(neu) => p.onEntwurf(mitWert(p.entwurf, ["seitenraender", r.id], neu))}
            onVerlassen={p.onFeldVerlassen} />
        );
      })}
      {p.faehig === true
        ? <SeiteEtappe2 {...p} fussFaehig={p.fussFaehig === true} onUnternehmen={p.onUnternehmen} />
        : <p className="lay__hinweis">{OHNE_FAEHIGKEIT} — Kopf- und Fußbereich, Beginn der
            Positionstabelle und Fußspalten stehen bereit, sobald sie aktualisiert ist.</p>}
    </div>
  );
}

/** Kopf- und Fußbereich: je Seitentyp eine Zahl; leer = Systemwert (der Seitenrand). */
function Bereich(p: PanelProps & { art: "kopfbereich" | "fussbereich" }): ReactElement {
  const kopf = p.art === "kopfbereich";
  const katalog = p.stand.katalog[p.art];
  const randId = kopf ? "oben" : "unten";
  const rand = mmWert(p.entwurf, ["seitenraender", randId], p.stand.katalog.seitenraender[randId]);
  const untergrenze = Math.max(rand, katalog.minMm);
  const name = kopf ? "Kopfbereich" : "Fußbereich";
  const behandelt = (pfad: string): boolean => SEITEN.some((s) => pfad === `${p.art}.${s.id}`);
  return (
    <fieldset className="lay__gruppe">
      <legend>{name}</legend>
      <p className="lay__panelhinweis">
        {kopf ? "Unterkante des Kopfbereichs ab der oberen Blattkante"
          : "Höhe des Fußbereichs ab der unteren Blattkante"}
        , von {mmText(untergrenze)} (Seitenrand, mindestens {mmText(katalog.minMm)}) bis{" "}
        {mmText(katalog.maxMm)} mm, in Zehntelmillimetern. Leer = wie der Seitenrand
        ({mmText(rand)} mm).</p>
      <div className="lay__grafikfelder">
        {SEITEN.map((s) => {
          const pfad = `${p.art}.${s.id}`;
          return (
            <ZahlFeld key={s.id} id={feldId(pfad)} label={`${name} ${s.text}`} einheit="mm"
              wert={wertAn(p.entwurf, [p.art, s.id])} platzhalter={mmText(rand)}
              fehler={p.feldFehler[pfad]} systemwertKnopf
              onWert={(neu) => p.onEntwurf(mitWert(p.entwurf, [p.art, s.id], neu))}
              onVerlassen={p.onFeldVerlassen} />
          );
        })}
      </div>
      <Befunde liste={restBefunde(p.feldFehler, p.art, behandelt)} />
    </fieldset>
  );
}

const BEGINN_TEXT = { fliessend: "im Fluss", folgeseite: "auf der ersten Folgeseite" } as const;

function Tabellenbeginn(p: PanelProps): ReactElement {
  const system = p.stand.katalog.positionstabelle.beginn;
  const andere = system === "fliessend" ? "folgeseite" : "fliessend";
  const roh = wertAn(p.entwurf, ["positionstabelle", "beginn"]);
  // Ein ausdrücklich gesetzter Systemwert zeigt sich als „Systemwert“ (kein eigener Wert).
  const wert = roh === andere ? andere : roh === system || roh === undefined ? "" : String(roh);
  // `positionstabelle.zebra` hat sein Feld im Register Tabelle und zählt dort.
  const behandelt = (pfad: string): boolean =>
    pfad === "positionstabelle.beginn" || pfad === "positionstabelle.zebra";
  return (
    <fieldset className="lay__gruppe">
      <legend>Positionstabelle</legend>
      <WahlFeld id={feldId("positionstabelle.beginn")} label="Positionstabelle beginnt"
        wert={wert}
        optionen={[
          { wert: "", text: `${BEGINN_TEXT[system]} (Systemwert)` },
          { wert: andere, text: BEGINN_TEXT[andere] },
          ...(wert !== "" && wert !== andere ? [{ wert, text: wert }] : []),
        ]}
        fehler={p.feldFehler["positionstabelle.beginn"]}
        onWahl={(w) => {
          p.onEntwurf(mitWert(p.entwurf, ["positionstabelle", "beginn"], w === "" ? undefined : w));
          p.onFeldVerlassen();
        }} />
      <p className="lay__panelhinweis">„Auf der ersten Folgeseite“: Seite 1 trägt Kopf,
        Anschreiben und Einleitung, die Tabelle beginnt oben auf Seite 2 — auch bei wenigen
        Positionen. Die Vorschau zeigt jede Seite des Musters bzw. des gewählten Belegs.</p>
      <Befunde liste={restBefunde(p.feldFehler, "positionstabelle", behandelt)} />
    </fieldset>
  );
}
const POSITION = { 2: ["links", "rechts"], 3: ["links", "Mitte", "rechts"] } as const;

const SEITENTYP: Record<FussSeitentyp, { titel: string; pfad: string }> = {
  erste: { titel: "Seite 1", pfad: "fusszeile.spalten" },
  folge: { titel: "Folgeseiten", pfad: "fusszeile.spaltenFolgeseiten" },
};

/** Ein Platzhalter der drei Fußblöcke aus Verwaltung › Unternehmen. */
const BLOCK = /\{fuss(links|mitte|rechts)\}/;

const texteVon = (spalten: readonly FussSpalte[]): string => spalten.map((s) => s.text ?? "").join("\n");

/**
 * Die Gruppe „Fußzeile" (Entscheide 75, 240, 243, 246): je Seitentyp ein Abschnitt mit Spaltenzahl, Breiten mit
 * Ausgleich und Text mit Platzhaltern — IMMER sichtbar und gefüllt, ohne „Systemfuß". Was angezeigt und was
 * geschrieben wird, entscheiden `fussAnzeige` und `mitFussSeitentyp` (layoutEntwurf.ts): Ohne Fußspalten im Entwurf
 * zeigt die Gruppe die Vorbelegung nur an und schreibt erst bei einer Änderung oder mit „Vorbelegung übernehmen".
 */
function Fusszeile(p: PanelProps & { onUnternehmen?: (() => void) | undefined }): ReactElement {
  const katalog = p.stand.katalog.fusszeile;
  const anzeige = fussAnzeige(p.entwurf, katalog.vorgabe);
  const ersteTexte = texteVon(anzeige.erste);
  const alleTexte = `${ersteTexte}\n${texteVon(anzeige.folge)}`;
  const doppeltFusszeile = ersteTexte.includes("{firmenfuss}") && ersteTexte.includes("{fusszeile}");
  const firmenfussUndBloecke = ersteTexte.includes("{firmenfuss}") && BLOCK.test(ersteTexte);
  const bloeckeLeer = p.stand.fussbloeckeLeer && BLOCK.test(alleTexte);

  function schreibe(typ: FussSeitentyp, neu: readonly FussSpalte[], fertig: boolean): void {
    p.onEntwurf(mitFussSeitentyp(p.entwurf, katalog.vorgabe, typ, neu, katalog.fussbereichMm));
    if (fertig) p.onFeldVerlassen();
  }
  const behandelt = (pfad: string): boolean => pfad === "fusszeile.spalten"
    || pfad === "fusszeile.spaltenFolgeseiten"
    || /^fusszeile\.(spalten|spaltenFolgeseiten)\[\d+\]\.(breite|text)$/.test(pfad);

  return (
    <fieldset className="lay__gruppe">
      <legend>Fußzeile</legend>
      {anzeige.stand === "vorbelegt" && (
        <>
          <Hinweis art="info" className="lay__meldung" aria-label="Fußzeile vorbelegt">
            Vorbelegung — noch nicht übernommen. Bis zur ersten Änderung trägt der Beleg den bisherigen Fuß.
          </Hinweis>
          <div className="lay__werkzeuge">
            <LayKnopf onClick={() => schreibe("erste", anzeige.erste, true)}>Vorbelegung übernehmen</LayKnopf>
          </div>
        </>
      )}
      {/* Warnungen oben — Probleme auf den ersten Blick, nicht unter der Platzhalterliste. */}
      {bloeckeLeer && (
        <Hinweis art="warnung" className="lay__meldung" aria-label="Fußblöcke leer">
          Die Fußblöcke von {p.stand.unternehmen.name} sind leer — {"{fusslinks}"}, {"{fussmitte}"} und{" "}
          {"{fussrechts}"} bleiben auf dem Beleg leer. Gepflegt werden sie unter Verwaltung › Unternehmen,
          „Fußblöcke für Belege“.
        </Hinweis>
      )}
      {bloeckeLeer && p.onUnternehmen !== undefined && (
        <div className="lay__werkzeuge">
          <LayKnopf onClick={p.onUnternehmen}>Fußblöcke pflegen</LayKnopf>
        </div>
      )}
      {firmenfussUndBloecke && (
        <Hinweis art="warnung" className="lay__meldung" aria-label="Firmenangaben doppelt">
          {"{firmenfuss}"} und die Fußblöcke stehen beide auf Seite 1 — die Firmenangaben erscheinen dann
          doppelt.
        </Hinweis>
      )}
      {doppeltFusszeile && (
        <Hinweis art="fehler" className="lay__meldung" aria-label="Fußzeile doppelt">
          {"{firmenfuss}"} enthält auf Seite 1 schon die Fußzeile — zusammen mit {"{fusszeile}"}{" "}
          steht sie dort doppelt.</Hinweis>
      )}
      <FussSeite {...p} typ="erste" spalten={anzeige.erste} onSpalten={schreibe}
        vorschlag={anzeige.stand === "vorbelegt"} />
      <FussSeite {...p} typ="folge" spalten={anzeige.folge} onSpalten={schreibe}
        gespiegelt={anzeige.stand === "folgeWieErste"} vorschlag={anzeige.stand === "vorbelegt"} />
      <ul className="lay__hilfe lay__platzhalter" aria-label="Platzhalter">
        {PLATZHALTER.map((h) => (
          <li key={h.name}><code>{h.name}</code> — {h.text}</li>
        ))}
      </ul>
      <p className="lay__panelhinweis">{"{firmenfuss}"} setzt die Firmenangaben fest aus den Stammdaten
        zusammen; {"{fusslinks}"}, {"{fussmitte}"} und {"{fussrechts}"} zeigen die drei Fußblöcke, die unter
        Verwaltung › Unternehmen frei gepflegt werden. Die Ausrichtung folgt der Lage: links, Mitte (bei drei
        Spalten), rechts. Mit ⏎ im Text beginnt eine neue Zeile.</p>
      <p className="lay__panelhinweis">Unten bleiben 5 mm Druckrand frei; was höher ist als der Fußbereich minus
        5 mm, schneidet die Engine ab. Drei Blöcke mit acht Zeilen brauchen auf Seite 1 etwa{" "}
        {katalog.fussbereichMm} mm Fußbereich.</p>
      <Befunde liste={restBefunde(p.feldFehler, "fusszeile", behandelt)} />
    </fieldset>
  );
}

/**
 * Ein Seitentyp der Fußzeile: Spaltenzahl (2 oder 3), je Spalte Breite und Text, Summe der Breiten. `vorschlag`
 * (Bestandslayout, Vorbelegung noch nicht übernommen): die Felder tragen gedämpfte, gestrichelte Rahmen statt der
 * Akzentfarbe gesetzter Werte, die Zwischenüberschrift sagt „Vorschlag" (Review 9, Hinweis 1).
 */
function FussSeite(p: PanelProps & {
  typ: FussSeitentyp; spalten: FussSpalte[]; gespiegelt?: boolean | undefined; vorschlag?: boolean | undefined;
  onSpalten: (typ: FussSeitentyp, neu: readonly FussSpalte[], fertig: boolean) => void;
}): ReactElement {
  const { titel, pfad } = SEITENTYP[p.typ];
  const katalog = p.stand.katalog.fusszeile;
  const summe = fussSumme(p.spalten);
  const summeOk = summe !== null && summe >= 99 && summe <= 101;
  const anzahl = p.spalten.length;
  const gueltigeZahl = anzahl === 2 || anzahl === 3;
  const listeFehler = p.feldFehler[pfad];
  return (
    <section className={`lay__fussseite${p.vorschlag === true ? " lay__fussseite--vorschlag" : ""}`}
      aria-label={`Fuß ${titel}`}>
      <h4 className="lay__fussseite-titel">{titel}{p.vorschlag === true ? " — Vorschlag" : ""}</h4>
      {p.gespiegelt === true && (
        <p className="lay__panelhinweis">Die Folgeseiten tragen den Fuß der Seite 1, bis hier etwas geändert
          wird.</p>
      )}
      <WahlFeld id={feldId(pfad)} label={`Spalten ${titel}`} wert={String(anzahl)}
        optionen={[
          { wert: "2", text: "2 Spalten" }, { wert: "3", text: "3 Spalten" },
          ...(gueltigeZahl ? [] : [{ wert: String(anzahl), text: `${anzahl} Spalten (ungültig)` }]),
        ]}
        fehler={listeFehler}
        onWahl={(w) => p.onSpalten(p.typ, fussSpaltenMitAnzahl(p.spalten, w === "2" ? 2 : 3), true)} />
      <ol className="lay__spalten lay__fussspalten">
        {p.spalten.map((s, i) => {
          const spaltenpfad = `${pfad}[${i}]`;
          const lage = gueltigeZahl ? POSITION[anzahl as 2 | 3][i] : undefined;
          const name = `${titel}, Fußspalte ${i + 1}`;
          return (
            <li key={i} className="lay__fussspalte-eintrag">
              <div className="lay__eingabezeile">
                <span className="lay__spaltenname">Fußspalte {i + 1}{lage === undefined ? "" : ` (${lage})`}</span>
                <LayEingabe id={feldId(`${spaltenpfad}.breite`)} name={`${name}: Breite`} zahl schmal
                  einheit="%" wert={zahlAnzeige(s.breite, "%")} fehler={p.feldFehler[`${spaltenpfad}.breite`]}
                  onText={(t) => p.onSpalten(p.typ, mitFussBreite(p.spalten, i, t), false)}
                  onVerlassen={p.onFeldVerlassen} />
              </div>
              <TextFeld id={feldId(`${spaltenpfad}.text`)} label={`Fußspalte ${i + 1}: Text`}
                name={`${name}: Text`}
                wert={s.text} systemwert="" mehrzeilig max={katalog.textMax}
                systemwertKnopf={false} fehler={p.feldFehler[`${spaltenpfad}.text`]}
                onWert={(neu) => p.onSpalten(p.typ, p.spalten.map((x, k) =>
                  (k !== i ? x : neu === undefined ? { breite: x.breite }
                    : { breite: x.breite, text: neu })), false)}
                onVerlassen={p.onFeldVerlassen} />
            </li>
          );
        })}
      </ol>
      <p className={`lay__summe lay__summe--${summeOk ? "ok" : "fehler"}`}>
        Summe der Breiten {titel}: {summe === null ? "—" : `${summe.toLocaleString("de-DE")} %`}
        {summeOk ? "" : summe === null ? " — eine Breite ist keine gültige Zahl"
          : " — erlaubt sind 100 % (± 1)"}
      </p>
      {listeFehler !== undefined && (
        <span className="lay__feldfehler" role="alert">{listeFehler}</span>
      )}
      {p.typ === "folge" && (
        <p className="lay__panelhinweis">Die Vorschau zeigt jede Seite: den Fuß der Folgeseiten ab Seite 2 —
          beim Muster, sobald es zwei Seiten hat (etwa mit „Positionstabelle beginnt auf der ersten
          Folgeseite“), sonst mit einem echten mehrseitigen Beleg.</p>
      )}
    </section>
  );
}

function SeiteEtappe2(
  p: PanelProps & { fussFaehig: boolean; onUnternehmen?: (() => void) | undefined },
): ReactElement {
  return (
    <>
      <Bereich {...p} art="kopfbereich" />
      <Bereich {...p} art="fussbereich" />
      <Tabellenbeginn {...p} />
      {p.fussFaehig
        ? <Fusszeile {...p} />
        : <p className="lay__hinweis">{OHNE_FAEHIGKEIT} — die Fußzeile je Seitentyp (Seite 1 und
            Folgeseiten) steht bereit, sobald sie aktualisiert ist.</p>}
    </>
  );
}
